import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isCurrencyCode, isMethodPayableForCount, type CurrencyCode } from 'pi-kiosk-shared';
import {
  useDonationProjectByCode,
  useListDonationProjectsWithCollected,
  useWhicheverDonationPreview,
} from '../../donation-projects/donationProjectsApi.js';
import {
  useCreateCheckoutSession,
  useStartGatewayPayment,
  useStartQrPayment,
} from '../../checkout/checkoutApi.js';
import {
  canShowCheckoutCurrencyPicker,
  isCheckoutCurrencyLocked,
  readStickyCheckoutCurrency,
  resolveAcceptedCheckoutCurrencies,
  resolveCheckoutCreateCurrency,
  resolveDisplayCurrencyFromSession,
  writeStickyCheckoutCurrency,
} from '../../checkout/checkoutCurrency.js';
import {
  fetchGatewayMethods,
  isGatewayMethodsPayable,
  type GatewayMethodsFetchResult,
} from '../../checkout/gatewayMethodsApi.js';
import {
  resolveVisibleCustomerPaymentMethods,
  useGatewayPaymentAvailable,
  fetchCustomerAccountDailyCashRemaining,
} from '../../checkout/paymentProvidersApi.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import { useCustomerErrorHandler } from '../../../shared/hooks/useCustomerErrorHandler.js';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import { useCustomerLocaleTag } from '../../../shared/hooks/useCustomerLocaleTag.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { readLastKnownSalesPointId } from '../../../shared/session/lastKnownSalesPoint.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import {
  donationStartedMetadata,
  emitDonationAbandoned,
  emitDonationAmountSelected,
  emitDonationProjectSelected,
  emitDonationScreenViewed,
  emitDonationTaxReceiptSelected,
  emitRecurringDonationSelected,
  resolveDonationProjectId,
} from '../../donation/donationAnalyticsMetadata.js';
import { emitPaymentSubmitted } from '../../shop/shopAnalyticsMetadata.js';
import { useShopKioskById } from '../../shop/shopApi.js';
import { resolveDonationRoutePaths } from '../../donation/donationRoutePaths.js';
import {
  buildAmountPresets,
  formatMinorAmount,
  readErrorCode,
  resolveInitialDonationStep,
  resolveVisibleDonationStep,
  type DonationJourneyStep,
  type DonationPaymentMethod,
} from '../phoneFirstDonationJourneyUtils.js';
import { SIMPLE_EMAIL_REGEX } from '../phoneFirstProductCheckoutUtils.js';

export type PhoneFirstDonationActiveStep = DonationJourneyStep;

export function usePhoneFirstDonationJourney() {
  const { t } = useTranslation('customer');
  const { session: authSession } = useCustomerSession();
  const api = useApiClient();
  const numberLocale = useCustomerLocaleTag();
  const params = useParams<{ donationProjectCode: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  const legacyCode = params.donationProjectCode ?? '';
  const hasLegacyCode = legacyCode.length > 0;
  const { embeddedInAccount, donatePath, projectsPath } = resolveDonationRoutePaths(
    tenantCode,
    location.pathname,
  );
  const startsOnProjectPicker = !hasLegacyCode && location.pathname === projectsPath;

  const salesPointId = useMemo((): number | null => {
    const lastKnown = readLastKnownSalesPointId(tenantCode);
    if (lastKnown === null || lastKnown <= 0) {
      return null;
    }
    return lastKnown;
  }, [tenantCode]);
  const kioskInfo = useShopKioskById(salesPointId);
  const paymentRailsMobile = kioskInfo.data?.paymentRailsMobile;
  const paymentSurfaceReadiness = kioskInfo.data?.paymentSurfaceReadiness;
  const gatewayPaymentAvailable = useGatewayPaymentAvailable(paymentRailsMobile);
  const customerRequireVerified = true;
  const qrRailCandidate =
    salesPointId !== null && salesPointId > 0 && paymentRailsMobile?.bankTransfer === true;

  const mobileQrAllowed =
    qrRailCandidate &&
    paymentSurfaceReadiness !== null &&
    paymentSurfaceReadiness !== undefined &&
    isMethodPayableForCount(
      paymentSurfaceReadiness.methods.bankTransfer,
      customerRequireVerified,
    );

  const gatewayRailCandidate =
    salesPointId !== null &&
    salesPointId > 0 &&
    gatewayPaymentAvailable &&
    paymentSurfaceReadiness !== null &&
    paymentSurfaceReadiness !== undefined &&
    isMethodPayableForCount(
      paymentSurfaceReadiness.methods.gateway,
      customerRequireVerified,
    );

  const donationGatewayCurrency = resolveCheckoutCreateCurrency(
    kioskInfo.data,
    salesPointId !== null ? readStickyCheckoutCurrency(salesPointId) : null,
  );
  const gatewayMethodsProbeEnabled =
    gatewayRailCandidate && salesPointId !== null && salesPointId > 0;
  const gatewayMethodsProbeQuery = useQuery({
    queryKey: customerQueryKeys.gatewayMethods(
      tenantCode,
      salesPointId ?? 0,
      donationGatewayCurrency,
      'DONATION',
    ),
    queryFn: async (): Promise<GatewayMethodsFetchResult> => {
      if (salesPointId === null || salesPointId <= 0) {
        throw new Error('Missing sales point for gateway methods probe');
      }
      return fetchGatewayMethods({
        apiBaseUrl,
        tenantCode,
        salesPointId,
        currency: donationGatewayCurrency,
        purpose: 'DONATION',
      });
    },
    enabled: gatewayMethodsProbeEnabled,
    staleTime: 30_000,
    retry: 1,
  });
  const gatewayMethodsPayable = ((): boolean | null => {
    if (!gatewayRailCandidate) {
      return false;
    }
    if (!gatewayMethodsProbeEnabled) {
      return null;
    }
    const probeSettled =
      gatewayMethodsProbeQuery.isSuccess || gatewayMethodsProbeQuery.isError;
    if (!probeSettled) {
      return null;
    }
    if (gatewayMethodsProbeQuery.isError) {
      return false;
    }
    return isGatewayMethodsPayable(gatewayMethodsProbeQuery.data);
  })();
  const gatewayMethodsProbePending =
    gatewayRailCandidate && gatewayMethodsPayable === null;
  const mobileGatewayAllowed =
    gatewayRailCandidate && gatewayMethodsPayable === true;
  const signedInCustomerId = authSession?.customerId ?? null;
  const dailyCashRemainingQuery = useQuery({
    queryKey: customerQueryKeys.customerAccountDailyCashRemaining(
      tenantCode,
      signedInCustomerId ?? 0,
    ),
    queryFn: () => fetchCustomerAccountDailyCashRemaining({ api }),
    enabled: signedInCustomerId != null,
    staleTime: 15_000,
    retry: 1,
  });

  const legacyProject = useDonationProjectByCode(hasLegacyCode ? legacyCode : null);

  const [step, setStep] = useState<DonationJourneyStep>(
    resolveInitialDonationStep(hasLegacyCode, startsOnProjectPicker),
  );
  const visibleStep = resolveVisibleDonationStep(
    step,
    hasLegacyCode,
    location.pathname,
    projectsPath,
    donatePath,
  );
  const [amountOverride, setAmountOverride] = useState<number | null>(null);

  const baseAmountMinor = useMemo(() => {
    const fromUrl = Number(search.get('amount'));
    if (Number.isFinite(fromUrl) && fromUrl > 0) {
      return fromUrl;
    }
    const def = legacyProject.data?.defaultAmountMinor;
    if (hasLegacyCode && typeof def === 'number' && def > 0) {
      return def;
    }
    return 10000;
  }, [search, hasLegacyCode, legacyProject.data?.defaultAmountMinor]);

  const amountMinor = amountOverride ?? baseAmountMinor;
  const visiblePaymentMethods = resolveVisibleCustomerPaymentMethods({
    bankQr: mobileQrAllowed,
    gateway: gatewayRailCandidate,
    walletPaymentsEnabled: false,
    paymentSurfaceReadiness,
    gatewayMethodsPayable,
    salesPointInteractionMode: kioskInfo.data?.salesPointInteractionMode,
    customerAuthenticated: signedInCustomerId != null,
    checkoutAmountMinor: amountMinor,
    customerAccountDailyCashRemainingMinor:
      dailyCashRemainingQuery.data?.customerAccountDailyCashRemainingMinor ?? null,
    customerAccountDailyCashRemainingResolved: dailyCashRemainingQuery.isSuccess,
  });
  const [selectedCode, setSelectedCode] = useState<string | null>(hasLegacyCode ? legacyCode : null);
  const [selectedMethod, setSelectedMethod] = useState<DonationPaymentMethod>('qr');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const [sessionCurrency, setSessionCurrency] = useState<CurrencyCode | null>(null);
  const [qrPayload, setQrPayload] = useState<string | null>(null);
  const [qrVisible, setQrVisible] = useState<boolean | null>(null);

  const showCheckoutCurrencyPicker = canShowCheckoutCurrencyPicker(kioskInfo.data);
  const checkoutCurrencyOptions = resolveAcceptedCheckoutCurrencies(kioskInfo.data);
  const stickyCurrency =
    salesPointId !== null ? readStickyCheckoutCurrency(salesPointId) : null;
  const derivedCheckoutCurrency = resolveCheckoutCreateCurrency(
    kioskInfo.data,
    stickyCurrency,
  );
  const [pickedCheckoutCurrency, setPickedCheckoutCurrency] = useState<CurrencyCode | null>(
    null,
  );
  const selectedCheckoutCurrency =
    pickedCheckoutCurrency !== null &&
    checkoutCurrencyOptions.includes(pickedCheckoutCurrency)
      ? pickedCheckoutCurrency
      : derivedCheckoutCurrency;
  const currencyLocked = isCheckoutCurrencyLocked({
    paymentId,
    hasActiveQr: qrPayload !== null,
    hasGateway: false,
  });
  const handleCheckoutCurrencySelect = useCallback(
    (nextCurrency: CurrencyCode): void => {
      if (currencyLocked) {
        return;
      }
      setPickedCheckoutCurrency(nextCurrency);
      if (salesPointId !== null && salesPointId > 0) {
        writeStickyCheckoutCurrency(salesPointId, nextCurrency);
      }
    },
    [currencyLocked, salesPointId, setPickedCheckoutCurrency],
  );
  const displayCurrency = resolveDisplayCurrencyFromSession(
    sessionCurrency,
    selectedCheckoutCurrency,
  );
  const [errorMessage, setErrorMessageState] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [whicheverError, setWhicheverErrorState] = useState<string | null>(null);

  /** Persist only public-safe copy (full G1 policy via toCustomerFacingErrorMessage). */
  const setErrorMessage = useCallback(
    (message: string | null): void => {
      if (message === null) {
        setErrorMessageState(null);
        return;
      }
      setErrorMessageState(
        toCustomerFacingErrorMessage(message, {
          fallback: t('donation.paymentStartFailed'),
        }),
      );
    },
    [t],
  );

  const setWhicheverError = useCallback(
    (message: string | null): void => {
      if (message === null) {
        setWhicheverErrorState(null);
        return;
      }
      setWhicheverErrorState(
        toCustomerFacingErrorMessage(message, {
          fallback: t('errors.donationWhicheverNotConfigured'),
        }),
      );
    },
    [t],
  );
  const [wantsTaxReceipt, setWantsTaxReceiptState] = useState(false);
  const [wantsRecurring, setWantsRecurringState] = useState(false);

  const projects = useListDonationProjectsWithCollected();
  const whicheverPreview = useWhicheverDonationPreview();
  const createSession = useCreateCheckoutSession();
  const startQr = useStartQrPayment();
  const startGateway = useStartGatewayPayment();
  const analytics = useCustomerAnalytics();
  const { handleError: logDonationError, reportVisibleError } = useCustomerErrorHandler(
    'donation.journey',
    {
      track: analytics.track,
      flow: 'DONATION',
      screenName: 'donation_amount',
      screen: 'donation_amount',
    },
  );
  const donationMountedRef = useRef(false);
  const paymentSubmittedRef = useRef(false);
  const donationAbandonedEmittedRef = useRef(false);
  const trackRef = useRef(analytics.track);
  const stepRef = useRef<DonationJourneyStep>(step);
  const amountMinorRef = useRef(amountMinor);
  const selectedProjectIdRef = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    trackRef.current = analytics.track;
    stepRef.current = step;
    amountMinorRef.current = amountMinor;
  }, [analytics.track, step, amountMinor]);

  useEffect(() => {
    if (hasLegacyCode && legacyProject.data?.id !== undefined) {
      selectedProjectIdRef.current = resolveDonationProjectId(legacyProject.data.id);
    }
  }, [hasLegacyCode, legacyProject.data?.id]);

  const rememberSelectedProjectId = (code: string | null): void => {
    if (code === null || code.length === 0) {
      selectedProjectIdRef.current = undefined;
      return;
    }
    const match = projects.data?.find((project) => project.code === code);
    if (match !== undefined) {
      selectedProjectIdRef.current = resolveDonationProjectId(match.id);
      return;
    }
    selectedProjectIdRef.current = undefined;
  };

  const resolveAbandonScreenName = (
    currentStep: DonationJourneyStep,
  ): 'donation_amount' | 'donation_project' | 'donation_method' | null => {
    if (currentStep === 'amount') {
      return 'donation_amount';
    }
    if (currentStep === 'project') {
      return 'donation_project';
    }
    if (currentStep === 'method') {
      return 'donation_method';
    }
    return null;
  };

  useEffect(() => {
    return () => {
      if (paymentSubmittedRef.current || donationAbandonedEmittedRef.current) {
        return;
      }
      const screenName = resolveAbandonScreenName(stepRef.current);
      if (screenName === null) {
        return;
      }
      const projectId = selectedProjectIdRef.current;
      const metadata: { project_id?: number; amount_cents?: number } = {
        amount_cents: amountMinorRef.current,
      };
      if (projectId !== undefined) {
        metadata.project_id = projectId;
      }
      emitDonationAbandoned(trackRef.current, screenName, metadata);
      donationAbandonedEmittedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (donationMountedRef.current) {
      return;
    }
    donationMountedRef.current = true;
    emitDonationScreenViewed(analytics.track, 'donation_amount');
    analytics.track({
      eventName: 'donation_started',
      flow: 'DONATION',
      screenName: 'donation_amount',
      metadata: donationStartedMetadata(hasLegacyCode ? legacyCode : null),
    });
  }, [analytics, hasLegacyCode, legacyCode]);

  const defaultWhicheverProject = whicheverPreview.data?.project ?? null;
  const amountPresets = useMemo(
    () => buildAmountPresets(legacyProject.data?.defaultAmountMinor),
    [legacyProject.data?.defaultAmountMinor],
  );

  const submitAmount = (): boolean => {
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      reportVisibleError(t('donation.positiveAmountRequired'), 'donationAmountInvalid');
      setErrorMessage(t('donation.positiveAmountRequired'));
      return false;
    }
    setErrorMessage(null);
    const usedPreset = amountOverride === null || amountPresets.includes(amountMinor);
    emitDonationAmountSelected(analytics.track, amountMinor, !usedPreset);
    const nextStep = hasLegacyCode ? 'method' : 'project';
    emitDonationScreenViewed(
      analytics.track,
      nextStep === 'method' ? 'donation_method' : 'donation_project',
    );
    if (!hasLegacyCode && nextStep === 'project') {
      navigate(projectsPath);
    }
    setStep(nextStep);
    return true;
  };

  const submitProject = (code: string | null): void => {
    setSelectedCode(code);
    rememberSelectedProjectId(code);
    emitDonationProjectSelected(analytics.track, code);
    emitDonationScreenViewed(analytics.track, 'donation_method');
    setStep('method');
  };

  const submitWhicheverProject = (): void => {
    if (whicheverPreview.data?.configured !== true) {
      const message = t('errors.donationWhicheverNotConfigured');
      setWhicheverError(message);
      reportVisibleError(message, 'donationWhicheverNotConfigured');
      return;
    }
    setWhicheverError(null);
    submitProject(null);
  };

  const resolveEmailOrFail = (): string | null => {
    const candidate = email.trim();
    if (candidate.length === 0) {
      setEmailError(null);
      return '';
    }
    if (!SIMPLE_EMAIL_REGEX.test(candidate)) {
      setEmailError(t('checkout.invalidEmail'));
      return null;
    }
    setEmailError(null);
    return candidate;
  };

  const setWantsTaxReceipt = (enabled: boolean): void => {
    setWantsTaxReceiptState(enabled);
    emitDonationTaxReceiptSelected(analytics.track, enabled);
  };

  const setWantsRecurring = (enabled: boolean): void => {
    setWantsRecurringState(enabled);
    if (enabled) {
      emitRecurringDonationSelected(analytics.track, 'monthly');
    }
  };

  const submitMethod = async (method: DonationPaymentMethod): Promise<void> => {
    setSelectedMethod(method);
    setErrorMessage(null);
    if (method === 'gateway' && !mobileGatewayAllowed) {
      const message = t('payment.cardUnavailable');
      reportVisibleError(message, 'gatewayMethodsNotPayable');
      setErrorMessage(message);
      return;
    }
    const resolvedEmail = resolveEmailOrFail();
    if (resolvedEmail === null) {
      return;
    }
    try {
      const session = await createSession.mutateAsync({
        amountMinor,
        currency: selectedCheckoutCurrency,
        purposeType: 'DONATION',
        flowType: 'PHONE_FIRST',
        entryChannel: 'PHONE_FIRST_PWA',
        donationProjectCode: selectedCode,
        ...(salesPointId !== null && salesPointId > 0 ? { salesPointId } : {}),
      });
      setCheckoutSessionId(session.id);
      setSessionCurrency(
        isCurrencyCode(session.currency) ? session.currency : selectedCheckoutCurrency,
      );
      analytics.bindSessionId(session.id);
      analytics.track({
        eventName: 'checkout_started',
        flow: 'DONATION',
        screenName: 'donation_method',
        metadata: {
          amountMinor,
          donationProjectCode: selectedCode,
        },
      });
      analytics.track({
        eventName: 'payment_started',
        flow: 'DONATION',
        screenName: 'donation_method',
        metadata: {
          paymentMethod: method === 'qr' ? 'QR' : 'CARD',
        },
      });
      if (method === 'qr') {
        const qr = await startQr.mutateAsync({
          sessionId: session.id,
          analyticsSessionId: session.id,
          ...(resolvedEmail.length > 0 ? { customerEmail: resolvedEmail } : {}),
        });
        setPaymentId(qr.paymentId);
        setQrPayload(qr.qrCodePayload ?? null);
        setQrVisible(qr.qrVisible ?? qr.qrAccess !== 'denied');
        paymentSubmittedRef.current = true;
        emitPaymentSubmitted(analytics.track, 'donation_method', 'QR', 'DONATION');
      } else {
        const gw = await startGateway.mutateAsync({
          sessionId: session.id,
          analyticsSessionId: session.id,
          ...(resolvedEmail.length > 0 ? { customerEmail: resolvedEmail } : {}),
        });
        setPaymentId(gw.paymentId);
        paymentSubmittedRef.current = true;
        emitPaymentSubmitted(analytics.track, 'donation_method', 'CARD', 'DONATION');
        window.location.assign(gw.redirectUrl);
        return;
      }
      setStep('pay');
      emitDonationScreenViewed(analytics.track, 'donation_pay');
    } catch (err) {
      if (readErrorCode(err) === 'TENANT_SWITCH_REDIRECT') {
        return;
      }
      // donationErrorMessage for known ApiError.code; never raw err.message in UI.
      const message = toCustomerFacingErrorMessage(err, {
        fallback: t('donation.paymentStartFailed'),
      });
      logDonationError(err, 'startPayment');
      setErrorMessage(message);
    }
  };

  const restart = (): void => {
    setStep('amount');
    if (!hasLegacyCode) {
      navigate(donatePath);
    }
    setSelectedCode(hasLegacyCode ? legacyCode : null);
    if (hasLegacyCode) {
      selectedProjectIdRef.current =
        legacyProject.data?.id !== undefined
          ? resolveDonationProjectId(legacyProject.data.id)
          : undefined;
    } else {
      selectedProjectIdRef.current = undefined;
    }
    setPaymentId(null);
    setCheckoutSessionId(null);
    setSessionCurrency(null);
    setQrPayload(null);
    setQrVisible(null);
    setErrorMessage(null);
    setEmail('');
    setEmailError(null);
    setWantsTaxReceiptState(false);
    setWantsRecurringState(false);
  };

  return {
    t,
    visibleStep,
    hasLegacyCode,
    legacyProject,
    embeddedInAccount,
    donatePath,
    projectsPath,
    navigate,
    tenantCode,
    defaultWhicheverProject,
    whicheverPreview,
    amountMinor,
    amountPresets,
    amountOverride,
    setAmountOverride,
    numberLocale,
    errorMessage,
    submitAmount,
    projects,
    whicheverError,
    submitWhicheverProject,
    submitProject,
    authSession,
    email,
    setEmail,
    emailError,
    selectedMethod,
    wantsTaxReceipt,
    setWantsTaxReceipt,
    wantsRecurring,
    setWantsRecurring,
    createSession,
    startQr,
    startGateway,
    submitMethod,
    paymentId,
    checkoutSessionId,
    qrPayload,
    qrVisible,
    showCheckoutCurrencyPicker,
    checkoutCurrencyOptions,
    selectedCheckoutCurrency,
    currencyLocked,
    handleCheckoutCurrencySelect,
    displayCurrency,
    restart,
    setStep,
    salesPointId,
    mobileQrAllowed,
    mobileGatewayAllowed,
    gatewayRailCandidate,
    gatewayMethodsProbePending,
    gatewayMethodsPayable,
    visiblePaymentMethods,
  };
}

export type UsePhoneFirstDonationJourneyResult = ReturnType<typeof usePhoneFirstDonationJourney>;

export { formatMinorAmount };
